import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type { ClinicId, RemoteQueueView } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

const PATIENT_LIMIT = 20

/** Fetches a single view of the remote dispensing queue. */
export function useRemoteQueue(opts: {
  clinicId: ClinicId | undefined
  view: RemoteQueueView
  page?: number
  q?: string
}) {
  const api = useOsApi()
  const { clinicId, view, page = 1, q } = opts

  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', 'remote', { clinicId, view, q, page }],
    queryFn: async () => {
      const res = await api.get('/orders/remote', {
        query: {
          clinicId: clinicId!,
          view,
          q,
          patientLimit: PATIENT_LIMIT,
          patientOffset: (page - 1) * PATIENT_LIMIT,
        },
      })
      if (!res.success) throw new Error(res.error.message)
      return res.data
    },
    placeholderData: keepPreviousData,
    enabled: !!clinicId,
  })

  return {
    orders: data?.orders,
    totalPatientCount: data?.totalPatientCount ?? 0,
    totalPages: Math.ceil((data?.totalPatientCount ?? 0) / PATIENT_LIMIT),
    isLoading,
    error,
  }
}
